"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Columns3,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  RotateCcw,
  X,
} from "lucide-react";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";
import { WhalesLogoMark } from "@/components/odds/whales";
import { comparisonColumnLabel, WHALES_COLUMN, type ComparisonColumnId } from "@/lib/whales";
import { COMPARISON_COLUMN_ORDER } from "./comparison-book-order";

export interface OptimizerComparisonOrderRuntime {
  order: readonly ComparisonColumnId[];
  hidden: readonly ComparisonColumnId[];
  isCustom: boolean;
  canWrite: boolean;
  isSaving: boolean;
  error: string | null;
  unavailableReason?: string | null;
  saveOrder(order: readonly ComparisonColumnId[], hidden: readonly ComparisonColumnId[]): Promise<boolean>;
  resetOrder(): Promise<boolean>;
}

function sameMembers(left: readonly ComparisonColumnId[], right: readonly ComparisonColumnId[]): boolean {
  return left.length === right.length && left.every((id) => right.includes(id));
}

function runtimeKey(runtime: OptimizerComparisonOrderRuntime): string {
  return `${runtime.order.join(",")}|${[...runtime.hidden].sort().join(",")}`;
}

interface ComparisonOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runtime: OptimizerComparisonOrderRuntime;
}

function ordersMatch(
  left: readonly ComparisonColumnId[],
  right: readonly ComparisonColumnId[],
): boolean {
  return left.length === right.length && left.every((book, index) => book === right[index]);
}

function moveBook(
  order: readonly ComparisonColumnId[],
  book: ComparisonColumnId,
  destinationIndex: number,
): ComparisonColumnId[] {
  const currentIndex = order.indexOf(book);
  if (currentIndex < 0) return [...order];
  const boundedIndex = Math.max(0, Math.min(order.length - 1, destinationIndex));
  if (currentIndex === boundedIndex) return [...order];

  const next = [...order];
  next.splice(currentIndex, 1);
  next.splice(boundedIndex, 0, book);
  return next;
}

function ComparisonOrderEditor({
  runtime,
  onCancel,
  onSaved,
}: {
  runtime: OptimizerComparisonOrderRuntime;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [draftOrder, setDraftOrder] = useState<ComparisonColumnId[]>(() => [...runtime.order]);
  const [draftHidden, setDraftHidden] = useState<ComparisonColumnId[]>(() => [...runtime.hidden]);
  const [resetRequested, setResetRequested] = useState(false);
  const [draggedBook, setDraggedBook] = useState<ComparisonColumnId | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [hasExternalChange, setHasExternalChange] = useState(false);
  const submitLockRef = useRef(false);
  const previousRuntimeRef = useRef({ key: runtimeKey(runtime), order: [...runtime.order], hidden: [...runtime.hidden] });

  useEffect(() => {
    const previous = previousRuntimeRef.current;
    if (previous.key === runtimeKey(runtime)) return;

    const hadDirtyDraft = resetRequested ||
      !ordersMatch(draftOrder, previous.order) ||
      !sameMembers(draftHidden, previous.hidden);
    previousRuntimeRef.current = { key: runtimeKey(runtime), order: [...runtime.order], hidden: [...runtime.hidden] };

    if (hadDirtyDraft) {
      setHasExternalChange(true);
      setDraggedBook(null);
      setAnnouncement(
        "Comparison order changed elsewhere. Your staged order was preserved.",
      );
      return;
    }

    setDraftOrder([...runtime.order]);
    setDraftHidden([...runtime.hidden]);
    setResetRequested(false);
    setHasExternalChange(false);
    setDraggedBook(null);
    setAnnouncement("");
  }, [draftOrder, draftHidden, resetRequested, runtime]);

  const canonicalOrder = useMemo(
    () => [...COMPARISON_COLUMN_ORDER],
    [],
  );
  const isCanonicalDraft = ordersMatch(draftOrder, canonicalOrder) && draftHidden.length === 0;
  const isDirty = resetRequested || !ordersMatch(draftOrder, runtime.order) || !sameMembers(draftHidden, runtime.hidden);
  const isPending = runtime.isSaving || isSubmitting;

  const updateBookPosition = (book: ComparisonColumnId, destinationIndex: number) => {
    const boundedIndex = Math.max(0, Math.min(draftOrder.length - 1, destinationIndex));
    setDraftOrder((current) => moveBook(current, book, boundedIndex));
    setAnnouncement(
      `${comparisonColumnLabel(book)} moved to position ${boundedIndex + 1}.`,
    );
    setResetRequested(false);
  };

  const toggleHidden = (book: ComparisonColumnId) => {
    const hide = !draftHidden.includes(book);
    setDraftHidden((current) => (hide ? [...current, book] : current.filter((id) => id !== book)));
    setAnnouncement(`${comparisonColumnLabel(book)} ${hide ? "hidden" : "shown"}.`);
    setResetRequested(false);
  };

  const handleApply = async () => {
    if (
      !isDirty ||
      isPending ||
      hasExternalChange ||
      !runtime.canWrite ||
      submitLockRef.current
    ) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    const success = await (isCanonicalDraft
      ? runtime.resetOrder()
      : runtime.saveOrder(draftOrder, draftHidden));
    submitLockRef.current = false;
    setIsSubmitting(false);
    if (!success) return;

    phCapture("optimizer_comparison_order_saved", {
      mode: isCanonicalDraft ? "default" : "custom",
      book_count: draftOrder.length,
      hidden_count: draftHidden.length,
    });
    onSaved();
  };

  const handleReset = () => {
    setDraftOrder(canonicalOrder);
    setDraftHidden([]);
    setResetRequested(runtime.isCustom || !ordersMatch(runtime.order, canonicalOrder) || runtime.hidden.length > 0);
    setAnnouncement("Default comparison order staged.");
  };

  const handleLoadLatest = () => {
    setDraftOrder([...runtime.order]);
    setDraftHidden([...runtime.hidden]);
    setResetRequested(false);
    setHasExternalChange(false);
    setDraggedBook(null);
    setAnnouncement("Latest saved comparison order loaded.");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <Columns3 className="size-4 shrink-0 text-primary" />
          <span>
            {draftOrder.length - draftHidden.length} of {draftOrder.length} comparison columns shown
          </span>
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={
            isPending ||
            !runtime.canWrite ||
            (!runtime.isCustom && isCanonicalDraft)
          }
          className={cn(
            "inline-flex min-h-11 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium sm:min-h-10",
            "text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            "disabled:cursor-not-allowed disabled:opacity-40",
          )}
        >
          <RotateCcw className="size-3.5" />
          Default order
        </button>
      </div>

      <ol
        className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-2 scrollbar-custom scrollbar-color-primary sm:px-4"
        aria-label="Comparison column order"
      >
        {draftOrder.map((book, index) => {
          const displayName = comparisonColumnLabel(book);
          const isDragged = draggedBook === book;
          const isHidden = draftHidden.includes(book);
          return (
            <li
              key={book}
              aria-posinset={index + 1}
              aria-setsize={draftOrder.length}
              onDragOver={(event) => {
                if (!draggedBook || draggedBook === book) return;
                event.preventDefault();
                setDraftOrder((current) => moveBook(current, draggedBook, index));
                setResetRequested(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (draggedBook) {
                  const position = draftOrder.indexOf(draggedBook) + 1;
                  setAnnouncement(
                    `${comparisonColumnLabel(draggedBook)} moved to position ${position}.`,
                  );
                }
                setDraggedBook(null);
              }}
              className={cn(
                "group flex min-h-12 items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors",
                isDragged
                  ? "border-primary/40 bg-primary/10 opacity-70"
                  : "border-white/[0.06] bg-white/[0.025] hover:border-white/[0.12] hover:bg-white/[0.045]",
              )}
            >
              <span className="w-6 shrink-0 text-center text-[11px] tabular-nums text-muted-foreground/60">
                {index + 1}
              </span>
              <span
                draggable={!isPending && runtime.canWrite}
                onDragStart={(event) => {
                  setDraggedBook(book);
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", book);
                }}
                onDragEnd={() => setDraggedBook(null)}
                title={`Drag ${displayName} to reorder`}
                className={cn(
                  "hidden h-9 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/45 sm:flex",
                  runtime.canWrite && !isPending
                    ? "cursor-grab hover:bg-white/[0.06] hover:text-foreground active:cursor-grabbing"
                    : "cursor-not-allowed opacity-40",
                )}
                aria-hidden="true"
              >
                <GripVertical className="size-4" />
              </span>
              {book === WHALES_COLUMN
                ? <WhalesLogoMark size={28} className={cn(isHidden && "opacity-40")} />
                : <SportsbookLogoMark sportsbook={book} size={28} decorative className={cn(isHidden && "opacity-40")} />}
              <span className={cn("min-w-0 flex-1 truncate text-sm font-medium", isHidden ? "text-foreground/40 line-through" : "text-foreground/90")}>
                {displayName}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleHidden(book)}
                  disabled={isPending || !runtime.canWrite}
                  aria-label={`${isHidden ? "Show" : "Hide"} ${displayName}`}
                  aria-pressed={isHidden}
                  className={cn(
                    "inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground",
                    "transition-colors hover:bg-white/[0.07] hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    "disabled:cursor-not-allowed disabled:opacity-25 sm:size-9",
                  )}
                >
                  {isHidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => updateBookPosition(book, index - 1)}
                  disabled={index === 0 || isPending || !runtime.canWrite}
                  aria-label={`Move ${displayName} up`}
                  className={cn(
                    "inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground",
                    "transition-colors hover:bg-white/[0.07] hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    "disabled:cursor-not-allowed disabled:opacity-25 sm:size-9",
                  )}
                >
                  <ChevronUp className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => updateBookPosition(book, index + 1)}
                  disabled={index === draftOrder.length - 1 || isPending || !runtime.canWrite}
                  aria-label={`Move ${displayName} down`}
                  className={cn(
                    "inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground",
                    "transition-colors hover:bg-white/[0.07] hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    "disabled:cursor-not-allowed disabled:opacity-25 sm:size-9",
                  )}
                >
                  <ChevronDown className="size-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {(runtime.error || runtime.unavailableReason) && (
        <p
          role="alert"
          className="mx-4 mb-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive sm:mx-5"
        >
          {runtime.error ?? runtime.unavailableReason}
        </p>
      )}

      {hasExternalChange && (
        <div
          role="alert"
          className="mx-4 mb-2 flex items-center justify-between gap-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-100 sm:mx-5"
        >
          <span>
            This order changed in another tab. Your staged changes are preserved.
          </span>
          <button
            type="button"
            onClick={handleLoadLatest}
            className="min-h-11 shrink-0 rounded-md px-2 font-semibold text-amber-50 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200/60 sm:min-h-9"
          >
            Load latest
          </button>
        </div>
      )}

      <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-white/[0.07] px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-5 sm:pb-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isPending}
          className={cn(
            "inline-flex min-h-11 items-center justify-center rounded-lg border border-white/[0.1] bg-white/[0.04] px-4 text-sm font-medium text-foreground",
            "transition-colors hover:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleApply()}
          disabled={!isDirty || isPending || hasExternalChange || !runtime.canWrite}
          className={cn(
            "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground",
            "transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
            "disabled:cursor-not-allowed disabled:opacity-45",
          )}
        >
          {isPending && <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />}
          {isPending ? "Applying…" : "Apply order"}
        </button>
      </div>
    </div>
  );
}

export function ComparisonOrderDialog({
  open,
  onOpenChange,
  runtime,
}: ComparisonOrderDialogProps) {
  const isMobile = useIsMobile();
  const presentationModeRef = useRef<"mobile" | "desktop" | null>(null);

  if (!open) {
    presentationModeRef.current = null;
  } else if (presentationModeRef.current === null) {
    presentationModeRef.current = isMobile ? "mobile" : "desktop";
  }

  const editor = (
    <ComparisonOrderEditor
      runtime={runtime}
      onCancel={() => onOpenChange(false)}
      onSaved={() => onOpenChange(false)}
    />
  );

  const useMobilePresentation = presentationModeRef.current === "mobile" ||
    (!open && isMobile === true);

  if (useMobilePresentation) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="h-[88svh] max-h-[88svh] overflow-hidden bg-[hsl(var(--optimizer-bg))]">
          <DrawerHeader className="items-start gap-3 px-5 pb-4">
            <div className="min-w-0 space-y-1">
              <DrawerTitle>Comparison column order</DrawerTitle>
              <DrawerDescription>
                Set the order once and hide the columns you skip. Every Optimizer keeps it and skips Books without odds.
              </DrawerDescription>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={runtime.isSaving}
              aria-label="Close comparison column order"
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-white/[0.06] hover:text-foreground disabled:opacity-40"
            >
              <X className="size-4" />
            </button>
          </DrawerHeader>
          {editor}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[88vh] max-w-xl px-4 sm:px-0"
        contentClassName="h-[min(760px,88vh)] min-h-0 gap-0 overflow-hidden p-0"
        closeButtonClassName="hidden"
      >
        <DialogHeader className="shrink-0 border-b border-white/[0.07] px-5 py-5 pr-14 text-left">
          <DialogTitle>Comparison column order</DialogTitle>
          <DialogDescription>
            Set the order once and hide the columns you skip. Every Optimizer keeps it and skips Books without odds.
          </DialogDescription>
        </DialogHeader>
        {editor}
      </DialogContent>
    </Dialog>
  );
}
